import { lazy } from 'react'

import {
  PerformanceGoalsEmployeeCrumbDocument,
  PerformanceGoalsMetricGroupCrumbDocument,
  PerformanceGoalsTabsDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { buildNavigation } from 'modules/core/lib/react-router'

import { EmployeeGoalsHeader } from 'modules/goals/components/EmployeeGoalsHeader'
import { GoalHeader } from 'modules/goals/components/GoalHeader'

const EmployeePage = lazy(() => import('modules/goals/pages/EmployeePage'))
const GoalDetailPageWrapper = lazy(
  () => import('modules/goals/revamp/pages/GoalDetailPage/GoalDetailPageWrapper')
)
const TeamDashboard = lazy(() => import('modules/goals/pages/TeamDashboard'))
const RootPage = lazy(() => import('modules/goals/pages/RootPage'))
const RedirectToRoutePage = lazy(() => import('modules/goals/pages/RedirectToRoutePage'))

export const goalsRoutes = buildNavigation((navigation) => {
  return [
    {
      path: navigation.goals.path.replace(`${navigation.performanceProcess.path}/`, ''),
      element: <RootPage />,
      handle: {
        meta: ({ watchQuery, session }) => {
          return watchQuery({
            query: PerformanceGoalsTabsDocument,
            variables: {
              currentEmployeeId: session?.employee?.id,
            },
          }).map(({ data }) => {
            const canSeeOtherEmployees = Boolean(data?.goals?.employeesConnection?.totalCount)
            return {
              title: i18n.t('performance_goals.pages.goals_index.title'),
              illustration: 'goals',
              description: i18n.t('performance_goals.pages.team_dashboard.description'),
              subNavigation: [
                {
                  title: i18n.t('performance_goals.tabs.people'),
                  href: navigation.goals.employees.fullPath(),
                  isVisible: canSeeOtherEmployees,
                  index: canSeeOtherEmployees,
                },
                {
                  title: i18n.t('performance_goals.sidebar.title'),
                  href: navigation.goals.groups.fullPath(),
                  isVisible: canSeeOtherEmployees,
                  index: !canSeeOtherEmployees,
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <RedirectToRoutePage />,
        },
        {
          path: navigation.goals.employees.path,
          children: [
            {
              index: true,
              element: <TeamDashboard section="people" />,
            },
            {
              path: navigation.goals.employees.employee.path,
              element: <EmployeePage />,
              handle: {
                resource: true,
                meta: ({ watchQuery, params }) => {
                  if (!params.id) return { title: '', description: '' }

                  return watchQuery({
                    query: PerformanceGoalsEmployeeCrumbDocument,
                    variables: {
                      id: params.id,
                    },
                  }).map(({ data }) => {
                    const goalsEmployee = data.goals?.employeesConnection?.nodes?.[0]
                    const employee = goalsEmployee?.employee

                    if (!employee) {
                      return { title: '' }
                    }

                    return {
                      title: employee.fullName ?? '',
                      description: employee.referenceContract?.jobTitle ?? '',
                      headerComponent: <EmployeeGoalsHeader goalsEmployee={goalsEmployee} />,
                    }
                  })
                },
              },
            },
          ],
        },
        {
          path: navigation.goals.groups.path,
          children: [
            {
              index: true,
              element: <TeamDashboard section="groups" />,
            },
            {
              path: navigation.goals.groups.group.path,
              element: <GoalDetailPageWrapper />,
              handle: {
                resource: true,
                meta: ({ watchQuery, params: { id }, session }) => {
                  if (!id) return { title: '' }

                  return watchQuery({
                    query: PerformanceGoalsMetricGroupCrumbDocument,
                    variables: {
                      id,
                      companyId: session.company.id,
                    },
                  }).map(({ data }) => {
                    const metricGroup = data?.goals?.metricGroup

                    return {
                      title: metricGroup?.title ?? '',
                      description: metricGroup?.description,
                      headerComponent: (
                        <GoalHeader
                          metricGroup={metricGroup}
                          features={data?.features?.featuresConnection?.nodes}
                        />
                      ),
                    }
                  })
                },
              },
            },
          ],
        },
      ],
    },
  ]
})
